import { useState } from 'react';
import { UserTargetAPI } from 'constants/trading';
import handleErrorMessageAPI from 'global/AlertErrorMessage';
import create from 'zustand';
import tradingStore from 'lib/stores/trading';
import tradingDataStore from 'lib/stores/tradingData';
import tradingModalStore from 'global/TradingModal/store';
import useSecuritiesCredentialStore from 'global/Securities/stores/useSecuritiesCredentialStore';
import shallow from 'zustand/shallow';
import useFliptStore from 'lib/flipt/store/useFliptStore';
import { FeatureFlagKeys } from 'lib/flipt/constants/flagKeys';
import useUIConfigStore from 'stores/useUIConfigStore';
import { LoginType } from 'typings/auth';
import { AUTH_MODAL_CONFIG } from '../constants';
import useAuthModalStore from '../store';
import { getTargetAPI } from '../../utils';

const useTradingStore = create(tradingStore);

interface NewCoreErrorResponse {
  response?: {
    data?: {
      error_type?: string;
      message?: string;
    };
  };
  cause?: {
    error_type?: string;
    message?: string;
  };
}

export default function useLogin() {
  const { resetErrorCode } = useTradingStore();
  const { getConfigFormula } = tradingDataStore();
  const showTradingModal = tradingModalStore((state) => state.showModal);
  const { closeModal, tradingModalConfig } = useAuthModalStore();
  const [loadLoginReal, loadLogout] = useSecuritiesCredentialStore(
    (s) => [s.loadLoginReal, s.loadLogout],
    shallow,
  );
  const loadBooleanEvaluation = useFliptStore((s) => s.loadBooleanEvaluation);
  const setUIValue = useUIConfigStore((s) => s.setUIValue);
  const getUIValue = useUIConfigStore((s) => s.getUIValue);
  const resetUIValue = useUIConfigStore((s) => s.resetUIValue);

  const [loading, setLoading] = useState(false);
  const [errorMsg, setErrorMsg] = useState('');

  const handleErrorNewCore = (error: NewCoreErrorResponse) => {
    const { error_type: errorType, message } =
      error?.response?.data ?? error?.cause ?? {};
    if (errorType === 'INVALID_CREDS') {
      setErrorMsg(message);
    }

    if (errorType !== 'INVALID_CREDS') {
      closeModal();
    }
  };

  const handleErrorLegacy = (message: string) => {
    if (message) {
      setErrorMsg(message);
    }

    if (!message) {
      closeModal();
    }
  };

  const checkDailyMaintenance = () =>
    tradingStore.getState().errorType === 'DAILY_MAINTENANCE_MODE';

  const onLogin = async (pin: string) => {
    setLoading(true);
    try {
      // Reset UI value because didn't reset when user logged out not from logout button
      await resetUIValue(LoginType.Securities);

      await loadLoginReal(pin);
      const isDailyMaintenance = checkDailyMaintenance();

      if (isDailyMaintenance) {
        loadLogout();
      } else {
        const { SPLIT_ORDER } = FeatureFlagKeys;

        await getConfigFormula();
        await loadBooleanEvaluation(SPLIT_ORDER);

        setErrorMsg('');
        resetErrorCode();
        handleErrorMessageAPI(
          'Kamu berhasil masuk ke Akun Trading',
          'plain-green',
        );

        // Show day trade onboarding
        setUIValue('dayTradeSubAccountActive', false);

        // Show day trade onboarding if user is eligible
        const isOnboardingShown = getUIValue('dayTradeTncShown');
        if (!isOnboardingShown) {
          showTradingModal({
            modalData: {
              showDayTradeOnboarding: true,
            },
          });
        }

        if (
          tradingModalConfig !== null &&
          !tradingModalConfig.onRealLoginSuccess
        ) {
          showTradingModal({
            ...tradingModalConfig,
            orderType: AUTH_MODAL_CONFIG.real[tradingModalConfig.orderType],
          });
        }

        if (tradingModalConfig?.onRealLoginSuccess) {
          tradingModalConfig.onRealLoginSuccess();
        }
      }

      closeModal();
    } catch (error) {
      const { message } = error?.cause ?? {};
      const isUserNewCore = getTargetAPI() === UserTargetAPI.NEW_CORE;

      if (!isUserNewCore) {
        handleErrorLegacy(message);
      } else {
        handleErrorNewCore(error);
      }
    }
    setLoading(false);
  };

  return {
    loading,
    setLoading,
    errorMsg,
    setErrorMsg,
    onLogin,
  };
}
